import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Table, InputGroup } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import Select from 'react-select';
import BarcodeModal from './BarcodeModal';
import AddProductModal from './AddProductModal';
import { toast } from 'react-toastify';
import './DebtSupplier.css'; // Reuse the chat styles
// import DebtSupplier from './DebtSupplier';
import SupplierHistoryTable from './SupplierHistoryTable';
import { useAuth } from '../contexts/AuthContext';

const ShoppingModal = ({ show, handleClose }) => {
  const { user } = useAuth(); // Get current user info
  const [taminlovchi, setTaminlovchi] = useState('');
  const [mahsulotlar, setMahsulotlar] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedProductForBarcode, setSelectedProductForBarcode] = useState(null);
  const [marginPercent, setMarginPercent] = useState('');
  const [marginActive, setMarginActive] = useState(false);
  const [marginTarget, setMarginTarget] = useState(null); // 'first' | 'sell' | null

  const [storehouses, setStorehouses] = useState([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);

  // Fetch suppliers, products, and storehouses on component mount
  useEffect(() => {
    if (show) {
      fetchSuppliers();
      fetchProducts();
      fetchStorehouses();
    }
  }, [show]);

  // When supplier changes, set all product currencies to supplier's currency
  useEffect(() => {
    if (!taminlovchi) return;
    const supplier = suppliers.find(s => (s.supplierid || s.id) === taminlovchi);
    if (!supplier) return;
    setProducts(products => products.map(p => ({ ...p, currency: supplier.currency || 'USD' })));
  }, [taminlovchi, suppliers]);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch('/api/suppliers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSuppliers(data.items || data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAllProducts(data.items || data);
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchStorehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // If user is owner, fetch all stores. Otherwise, fetch permitted stores.
      const isOwner = user && user.role === 'owner';
      const endpoint = isOwner ? '/api/stores' : '/api/store-permissions/import-stores';

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stores: ${response.status}`);
      }

      const data = await response.json();
      // Handle both response formats: {items: [...]} or just [...]
      setStorehouses(data.items || data || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      // Optionally, show a toast notification to the user
    }
  };

  // Convert suppliers to react-select format
  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.supplierid || supplier.id,
    label: supplier.name
  }));

  // Convert products to react-select format
  const productOptions = allProducts
    .filter(product => {
      if (product.productid === undefined) {
        console.error('Product without productid:', product);
        return false;
      }
      return true;
    })
    .map(product => ({
      value: product.productid,
      label: product.name,
      product: product
    }));

  // Convert storehouses to react-select format
  const storeOptions = storehouses.map(store => ({
    value: store.StorehouseID || store.id || store.storehouse_id,
    label: store.name
  }));

  const handleProductSelection = (selectedOptions) => {
    console.log('Selected options:', selectedOptions); // Added for debugging
    // Create a new array of selected product IDs
    const selectedProductIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setMahsulotlar(selectedProductIds);

    const supplier = suppliers.find(s => (s.supplierid || s.id) === taminlovchi);
    const supplierCurrency = supplier?.currency || 'USD';

    // Create new products array from selected options only
    const selectedProductsData = selectedOptions ? selectedOptions.map(option => {
      const product = option.product;
      // Check if this product already exists in the products array to preserve user-entered data
      const existingProduct = products.find(p => p.id === product.productid);
      return {
        id: product.productid,
        name: product.name,
        code: product.barcode || '',
        quantity: existingProduct ? existingProduct.quantity : '',
        currency: supplierCurrency,
        buyPrice: existingProduct ? existingProduct.buyPrice : '',
        firstPrice: existingProduct ? existingProduct.firstPrice : '',
        sellPrice: existingProduct ? existingProduct.sellPrice : '',
        updateSellingPrice: existingProduct ? existingProduct.updateSellingPrice : false,
        totalSell: 0,
        costing_method: product.costing_method || 'FIFO',
        barcodes: existingProduct ? existingProduct.barcodes : [],
        store_id: existingProduct ? existingProduct.store_id : '',
        barcodesFinalized: existingProduct ? existingProduct.barcodesFinalized : false
      };
    }) : [];

    // Update products state to only include currently selected products
    setProducts(selectedProductsData);
    console.log('Updated mahsulotlar:', selectedProductIds); // Added for debugging
    console.log('Updated products:', selectedProductsData); // Added for debugging
  };

  // Calculate total price for a product (quantity × buy price)
  const calculateProductTotal = (quantity, buyPrice) => {
    return parseFloat(quantity || 0) * parseFloat(buyPrice || 0);
  };

  // Safely parse numbers; treat empty/invalid as 0 to preserve previous behavior on submit
  const toNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  // Calculate grand total by currency
  const calculateGrandTotalByCurrency = () => {
    const totals = {};
    products.forEach(product => {
      const total = calculateProductTotal(product.quantity, product.buyPrice);
      const currency = product.currency || 'UZS';
      totals[currency] = (totals[currency] || 0) + total;
    });
    return totals;
  };

  // Format total display
  const formatTotalDisplay = () => {
    const totals = calculateGrandTotalByCurrency();
    const currencies = Object.keys(totals);

    if (currencies.length === 0) return '0 UZS';
    if (currencies.length === 1) {
      return `${totals[currencies[0]].toLocaleString()} ${currencies[0]}`;
    }

    // Multiple currencies
    return currencies.map(currency =>
      `${totals[currency].toLocaleString()} ${currency}`
    ).join(' + ');
  };

  // Handle barcode modal for Specific method products
  const handleBarcodeButtonClick = (productIndex) => {
    setSelectedProductForBarcode(productIndex);
    setShowBarcodeModal(true);
  };

  const handleBarcodeSave = (barcodes) => {
    const newProducts = [...products];
    if (selectedProductForBarcode !== null) {
      newProducts[selectedProductForBarcode].barcodes = barcodes;
      newProducts[selectedProductForBarcode].barcodesFinalized = true;
    }
    setProducts(newProducts);
    setShowBarcodeModal(false);
  };

  const handleSubmit = async () => {
    if (!taminlovchi) {
      alert('Taminlovchi tanlang');
      return;
    }

    if (products.length === 0) {
      alert('Mahsulot tanlang');
      return;
    }

    // Check if user is authenticated
    if (!user) {
      alert('Iltimos, avval tizimga kiring');
      return;
    }

    const supplier = suppliers.find(s => (s.supplierid || s.id) === taminlovchi);
    const supplierCurrency = supplier?.currency || 'USD';

    try {
      // Prepare products data for bulk purchase
      const productsToSave = products.map(product => {
        // Stricter check for store_id
        if (product.store_id === undefined || product.store_id === null || product.store_id === '') {
          throw new Error(`Mahsulot uchun joy tanlanmagan: ${product.name}`);
        }
        const storeId = Number(product.store_id);
        if (isNaN(storeId) || storeId <= 0) {
          throw new Error(`Noto'g'ri joy ID raqami: ${product.name} (${product.store_id})`);
        }
        const storeExists = storehouses.some(store =>
          store.id === storeId ||
          store.StorehouseID === storeId ||
          store.storehouse_id === storeId
        );
        if (!storeExists) {
          throw new Error(`Tanlangan joy topilmadi: ${product.name} (ID: ${product.store_id})`);
        }
        return {
          product_id: product.id,
          quantity_purchased: toNum(product.quantity),
          purchase_price: toNum(product.buyPrice),
          first_price: toNum(product.firstPrice),
          selling_price: toNum(product.sellPrice),
          update_selling_price: product.updateSellingPrice,
          barcodes: product.barcodes || [],
          costing_method: product.costing_method,
          currency: supplierCurrency,
          store_id: storeId
        };
      });

      console.log('DEBUG: productsToSave', productsToSave);
      if (productsToSave.length === 0) {
        alert('Hech qanday mahsulot saqlash uchun tayyor emas');
        return;
      }

      // Use invoice creation endpoint
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`, // Include auth token
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        },
        body: JSON.stringify({
          products: productsToSave,
          supplier_id: taminlovchi,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Xarid saqlashda xatolik yuz berdi');
      }

      const result = await response.json();
      console.log('Bulk purchase result:', result);
      alert('Invoys muvaffaqiyatli yaratildi!');
      handleClose();
    } catch (error) {
      console.error('Error saving purchase:', error);
      alert(`Xatolik yuz berdi: ${error.message}`);
    }
  };

  const handleAddNewProduct = async (productData) => {
    try {
      setSavingProduct(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        },
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add product');
      }

      const newProduct = await response.json();
      toast.success('Mahsulot muvaffaqiyatli qo\'shildi');

      // Refresh products list
      await fetchProducts();

      // Auto-select the new product
      const productId = newProduct.productid || newProduct.id;
      if (productId) {
        setMahsulotlar(prev => [...prev, productId]);

        const supplier = suppliers.find(s => (s.supplierid || s.id) === taminlovchi);
        const supplierCurrency = supplier?.currency || 'USD';

        const newProductEntry = {
          id: productId,
          name: newProduct.name,
          code: newProduct.barcode || '',
          quantity: '',
          currency: supplierCurrency,
          buyPrice: '',
          firstPrice: '',
          sellPrice: '',
          updateSellingPrice: false,
          totalSell: 0,
          costing_method: newProduct.costing_method || 'FIFO',
          barcodes: [],
          store_id: '',
          barcodesFinalized: false
        };

        setProducts(prev => [...prev, newProductEntry]);
      }

      setShowAddProductModal(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  // Margin %: apply to focused target (first price or sell price)
  const handleMarginFocus = (target) => {
    // Only activate if buy price exists (cursor moves to target input)
    setMarginTarget(target);
    const hasBuy = products.some(p => parseFloat(p.buyPrice) > 0);
    setMarginActive(hasBuy);
  };

  const applyMarginToProduct = (base, percent) => {
    const b = Number(base) || 0;
    const p = Number(percent) || 0;
    return Number((b * (1 + p / 100)).toFixed(2));
  };

  const handleMarginChange = (e) => {
    let v = String(e.target.value || '').replace(',', '.');
    v = v.replace(/[^0-9.\-]/g, '');
    // Only one dot
    const parts = v.split('.');
    if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
    setMarginPercent(v);
    const percent = parseFloat(v);
    if (isNaN(percent) || !marginTarget) return;
    // Apply per-row based on buyPrice
    setProducts(prev => prev.map(p => {
      const buy = Number(p.buyPrice) || 0;
      if (buy <= 0) return p;
      if (marginTarget === 'first') {
        return { ...p, firstPrice: applyMarginToProduct(buy, percent) };
      }
      if (marginTarget === 'sell') {
        return { ...p, sellPrice: applyMarginToProduct(buy, percent) };
      }
      return p;
    }));
  };

  const clearMargin = () => {
    setMarginPercent('');
    setMarginActive(false);
    setMarginTarget(null);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Mahsulot import qilish</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row d-flex ">
            <div className="col-6" style={{ height: '400px', overflowY: 'scroll' }}>
              {taminlovchi ? (
                <SupplierHistoryTable supplierId={taminlovchi} />
              ) : (
                <div className="text-center text-muted mt-5">Taminlovchi tanlang</div>
              )}
            </div>
            <div className="col-6">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Taminlovchi</Form.Label>
                  <Select
                    value={supplierOptions.find(option => option.value === taminlovchi)}
                    onChange={(selectedOption) => setTaminlovchi(selectedOption ? selectedOption.value : '')}
                    options={supplierOptions}
                    placeholder="Taminlovchi tanlang..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isLoading={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mahsulot qo'shish</Form.Label>
                  <InputGroup>
                    <div style={{ flex: 1 }}>
                      <Select
                        isMulti
                        value={mahsulotlar.map(id => productOptions.find(option => option.value === id)).filter(Boolean)}
                        onChange={handleProductSelection}
                        options={productOptions}
                        placeholder="Mahsulot tanlang..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isLoading={loading}
                      />
                    </div>
                    <Button variant="outline-success" onClick={() => setShowAddProductModal(true)}>
                      <FaPlus />
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Form>

              <div className="mt-3 d-flex align-items-center justify-content-between">
                <strong>Total: {formatTotalDisplay()}</strong>
                <div className="d-flex align-items-center gap-2">
                  <span className="me-2">Marja %</span>
                  <Form.Control
                    type="number"
                    value={marginPercent}
                    onChange={handleMarginChange}
                    disabled={!marginActive}
                    style={{ width: '120px' }}
                    placeholder="%"
                  />
                  <Button variant="outline-secondary" size="sm" onClick={clearMargin} disabled={!marginActive}>Clear</Button>
                </div>
              </div>
            </div>
          </div>
          <div style={{ height: '200px', overflowY: 'scroll' }}>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nom</th>
                  <th>Joy</th>
                  <th>Miqdor</th>
                  <th>Pul birligi {taminlovchi && suppliers.find(s => (s.supplierid || s.id) === taminlovchi)?.currency ? `(${suppliers.find(s => (s.supplierid || s.id) === taminlovchi)?.currency})` : ''}</th>
                  <th>Sotib olish narxi</th>
                  <th>Ulgrurchi narx</th>
                  <th>Sotish narxi</th>
                  <th>Barcode</th>
                  <th>Umumiy narx</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => {
                  const productTotal = calculateProductTotal(product.quantity, product.buyPrice);
                  return (
                    <tr key={product.id}>
                      <td>{index + 1}</td>
                      <td>{product.name} </td>
                      <td>
                        <Select
                          value={storeOptions.find(option => option.value === product.store_id) || null}
                          onChange={selectedOption => {
                            const newProducts = [...products];
                            newProducts[index].store_id = selectedOption ? selectedOption.value : '';
                            setProducts(newProducts);
                          }}
                          options={storeOptions}
                          placeholder="Joy tanlang"
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable
                        />
                      </td>
                      <td><Form.Control
                        type="text"
                        inputMode={product.costing_method === 'FIFO' ? 'decimal' : 'numeric'}
                        placeholder={product.costing_method === 'FIFO' ? '0.000' : '0'}
                        value={product.quantity}
                        onChange={(e) => {
                          const newProducts = [...products];
                          let v = String(e.target.value || '');
                          if (product.costing_method === 'FIFO') {
                            v = v.replace(',', '.');
                            v = v.replace(/[^0-9.]/g, '');
                            const parts = v.split('.');
                            if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
                            const dotIndex = v.indexOf('.');
                            if (dotIndex !== -1) {
                              const intPart = v.slice(0, dotIndex);
                              const decPart = v.slice(dotIndex + 1, dotIndex + 1 + 3);
                              v = `${intPart}.${decPart}`;
                            }
                          } else {
                            v = v.replace(/\D/g, ''); // integers only
                          }
                          newProducts[index].quantity = v;
                          setProducts(newProducts);
                        }}
                        onBlur={(e) => {
                          const newProducts = [...products];
                          let v = String(e.target.value || '');
                          if (product.costing_method === 'FIFO') {
                            v = v.replace(',', '.');
                            v = v.replace(/[^0-9.]/g, '');
                            if (v === '') v = '0';
                            const num = parseFloat(v);
                            newProducts[index].quantity = isNaN(num) ? 0 : Number(num.toFixed(3));
                          } else {
                            v = v.replace(/\D/g, '');
                            const num = parseInt(v, 10);
                            newProducts[index].quantity = isNaN(num) ? 0 : num;
                          }
                          setProducts(newProducts);
                        }}
                        disabled={product.barcodesFinalized}
                      />
                      </td>
                      <td>
                        <span>{suppliers.find(s => (s.supplierid || s.id) === taminlovchi)?.currency || 'UZS'}</span>
                      </td>
                      <td><Form.Control type="number" value={product.buyPrice} onChange={(e) => {
                        const newProducts = [...products];
                        newProducts[index].buyPrice = e.target.value;
                        setProducts(newProducts);
                      }} /></td>
                      <td><Form.Control type="number" value={product.firstPrice} onFocus={() => handleMarginFocus('first')} onChange={(e) => {
                        const newProducts = [...products];
                        newProducts[index].firstPrice = e.target.value;
                        setProducts(newProducts);
                      }} /></td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="number"
                            value={product.sellPrice}
                            onFocus={() => handleMarginFocus('sell')}
                            onChange={(e) => {
                              const newProducts = [...products];
                              newProducts[index].sellPrice = e.target.value;
                              setProducts(newProducts);
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <Form.Check
                            type="switch"
                            id={`updateSellingPrice-${product.id}`}
                            checked={product.updateSellingPrice}
                            onChange={(e) => {
                              const newProducts = [...products];
                              newProducts[index].updateSellingPrice = e.target.checked;
                              setProducts(newProducts);
                            }}
                            title="Sotish narxini barcha partiyalarga qo'llash"
                          />
                        </div>
                      </td>
                      <td>
                        {product.costing_method === 'FIFO' ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <div>
                            {product.barcodes.length > 0 ? (
                              <span className="badge bg-success">{product.barcodes.length} barcode</span>
                            ) : (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleBarcodeButtonClick(index)}
                                disabled={product.barcodesFinalized || !product.quantity || isNaN(product.quantity) || Number(product.quantity) <= 0}
                              >
                                Barcode qo'shish
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{productTotal.toLocaleString()} {product.currency}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>


        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Bekor qilish
          </Button>
          <Button variant="success" onClick={handleSubmit}>
            Tasdiqlsh
          </Button>
        </Modal.Footer>
      </Modal>

      <BarcodeModal
        show={showBarcodeModal}
        handleClose={() => setShowBarcodeModal(false)}
        onSave={handleBarcodeSave}
        productName={selectedProductForBarcode !== null ? products[selectedProductForBarcode]?.name : ''}
        requiredQuantity={selectedProductForBarcode !== null ? products[selectedProductForBarcode]?.quantity : null}
      />

      <AddProductModal
        show={showAddProductModal}
        handleClose={() => setShowAddProductModal(false)}
        handleAddProduct={handleAddNewProduct}
        saving={savingProduct}
      />
    </>
  );
};

export default ShoppingModal;